// © 2026 KaraokeClubs. All rights reserved.

"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { t } from "@/lib/i18n";
import {
  getKaraokeStatus,
  getVenueName,
  getVenueDescription,
  getScheduleNote,
  getPriceLabel,
  DISTRICT_LABELS,
} from "@/lib/venue-helpers";
import { formatDistance } from "@/lib/geo";
import type { Venue, Locale } from "@/lib/types";
import StatusBadge, { ConfidenceDots, ScheduleNote } from "@/components/StatusBadge";
import WeeklyGrid from "@/components/WeeklyGrid";

interface VenueCardProps {
  venue: Venue;
  locale: Locale;
  index: number;
  featured?: boolean;
  compact?: boolean;
  noLink?: boolean;
  /** Distance from the user in km, when geolocation is active. */
  distanceKm?: number | null;
}

const TYPE_ICONS: Record<string, string> = {
  karaoke_bar: "\u{1F3A4}",
  private_room: "\u{1F3B6}",
  restaurant_karaoke: "\u{1F37D}",
  open_mic: "\u{1F3B5}",
};

/** A small pin + "1.2 km" chip shown when we know the user's location. */
function DistanceBadge({ km, className = "" }: { km: number | null | undefined; className?: string }) {
  const label = formatDistance(km ?? null);
  if (!label) return null;
  return (
    <span
      className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold
                  bg-sky-500/10 text-sky-300 border border-sky-500/15 ${className}`}
    >
      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
      {label}
    </span>
  );
}

export default function VenueCard({ venue, locale, index, featured, compact, noLink, distanceKm }: VenueCardProps) {
  const status = getKaraokeStatus(venue);
  const name = getVenueName(venue, locale);
  const description = getVenueDescription(venue, locale);
  const isOpen = status ? (status.state === "open" || status.state === "closing_soon") : false;
  const typeIcon = TYPE_ICONS[venue.venue_type] || "\u{1F3A4}";

  // ── Compact: map carousel card ──
  if (compact) {
    const inner = (
      <div className={`card-shine rounded-2xl p-4 border transition-all duration-200 ${
        isOpen
          ? "bg-gradient-to-br from-zinc-900 to-emerald-950/20 border-emerald-500/20 shadow-lg shadow-emerald-900/10"
          : "bg-zinc-900 border-white/[0.06]"
      }`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base shrink-0">{typeIcon}</span>
            <h3 className="font-bold text-sm truncate">{name}</h3>
          </div>
          {status ? <StatusBadge status={status} locale={locale} /> : (
            <span className="shrink-0 inline-flex items-center rounded-full font-medium px-2.5 py-1 text-xs gap-1.5 status-closed">
              {t(locale, "schedule_unknown")}
            </span>
          )}
        </div>
        <p className="text-[11px] text-zinc-500 truncate mb-2 flex items-center gap-1.5">
          <span className="truncate">{DISTRICT_LABELS[venue.district] ?? venue.district} &middot; {venue.address}</span>
          <DistanceBadge km={distanceKm} />
        </p>
        <div className="flex items-center gap-1.5">
          <span className="px-2 py-0.5 rounded text-[10px] bg-violet-500/10 text-violet-400 font-medium">
            {t(locale, `venue_type_${venue.venue_type}`)}
          </span>
          {venue.price_range && (
            <span className="px-2 py-0.5 rounded text-[10px] bg-amber-500/10 text-amber-400 font-medium">
              {getPriceLabel(venue.price_range)}
            </span>
          )}
          {venue.has_private_rooms && (
            <span className="px-2 py-0.5 rounded text-[10px] bg-white/[0.05] text-zinc-400">
              {t(locale, "private_rooms")}
            </span>
          )}
        </div>
      </div>
    );
    if (noLink) return inner;
    return <Link href={`/venue/${venue.slug}`} className="block">{inner}</Link>;
  }

  // ── Featured: top pick ──
  if (featured) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      >
        <Link href={`/venue/${venue.slug}`} data-umami-event="venue-card-click" data-umami-event-venue={venue.slug} className="block group">
          <div className="card-shine relative rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-zinc-900 via-zinc-900 to-emerald-950/20 p-5 transition-all duration-200 active:scale-[0.99] overflow-hidden">
            {/* Subtle gradient orb */}
            <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-emerald-500/[0.07] blur-2xl pointer-events-none" />
            <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-violet-500/[0.05] blur-2xl pointer-events-none" />

            <div className="relative">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                  {t(locale, "top_pick")}
                </span>
                {status ? <StatusBadge status={status} locale={locale} size="sm" /> : (
                  <span className="shrink-0 inline-flex items-center rounded-full font-medium px-2.5 py-1 text-xs gap-1.5 status-closed">
                    {t(locale, "schedule_unknown")}
                  </span>
                )}
                <DistanceBadge km={distanceKm} />
              </div>

              <div className="flex items-start gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border border-violet-500/20 flex items-center justify-center text-lg shrink-0">
                  {typeIcon}
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-extrabold tracking-tight group-hover:text-emerald-300 transition-colors">
                    {name}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-zinc-500">
                    <span>{DISTRICT_LABELS[venue.district] ?? venue.district}</span>
                    <span className="text-zinc-700">&middot;</span>
                    <span>{venue.address}</span>
                    <ConfidenceDots confidence={venue.karaoke_schedule_confidence} />
                  </div>
                </div>
              </div>

              {description && (
                <p className="text-sm text-zinc-400 line-clamp-2 leading-relaxed mb-2">{description}</p>
              )}

              {getScheduleNote(venue, locale) && (
                <ScheduleNote note={getScheduleNote(venue, locale)} className="mb-2.5" />
              )}

              <div className="flex flex-wrap items-center gap-1.5">
                <span className="px-2.5 py-1 rounded-lg text-[11px] bg-violet-500/10 text-violet-400 font-medium border border-violet-500/15">
                  {t(locale, `venue_type_${venue.venue_type}`)}
                </span>
                {venue.price_range && (
                  <span className="px-2.5 py-1 rounded-lg text-[11px] bg-amber-500/10 text-amber-400 font-medium border border-amber-500/15">
                    {getPriceLabel(venue.price_range)}
                  </span>
                )}
                {venue.has_private_rooms && (
                  <span className="px-2.5 py-1 rounded-lg text-[11px] bg-white/[0.05] text-zinc-400 border border-white/[0.06]">
                    {t(locale, "private_rooms")}
                  </span>
                )}
                {venue.has_english_songs && (
                  <span className="px-2.5 py-1 rounded-lg text-[11px] bg-white/[0.05] text-zinc-400 border border-white/[0.06]">
                    {t(locale, "english_songs")}
                  </span>
                )}
                {venue.song_count && venue.song_count > 0 && (
                  <span className="px-2.5 py-1 rounded-lg text-[11px] bg-white/[0.05] text-zinc-400 border border-white/[0.06]">
                    {venue.song_count.toLocaleString()} songs
                  </span>
                )}
              </div>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  // ── Regular card ──
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.03, 0.2), ease: [0.16, 1, 0.3, 1] }}
    >
      <Link href={`/venue/${venue.slug}`} data-umami-event="venue-card-click" data-umami-event-venue={venue.slug} className="block group">
        <div className={`card-shine rounded-xl border bg-zinc-900/70 p-4 transition-all duration-150 active:scale-[0.99] active:bg-zinc-800/80 group-hover:border-white/[0.12] overflow-hidden ${
          isOpen
            ? "border-white/[0.06] group-hover:shadow-lg group-hover:shadow-emerald-900/5"
            : "border-white/[0.04] opacity-60"
        }`}>
          <div className="flex items-start gap-3 mb-2">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-base shrink-0 ${
              isOpen
                ? "bg-gradient-to-br from-violet-500/15 to-fuchsia-500/15 border border-violet-500/15"
                : "bg-zinc-800/50 border border-white/[0.04]"
            }`}>
              {typeIcon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-0.5">
                <h3 className="font-bold text-[15px] leading-tight group-hover:text-violet-300 transition-colors truncate">{name}</h3>
                {status ? <StatusBadge status={status} locale={locale} /> : (
                  <span className="shrink-0 inline-flex items-center rounded-full font-medium px-2.5 py-1 text-xs gap-1.5 status-closed">
                    {t(locale, "schedule_unknown")}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-[13px] text-zinc-500">
                <span>{DISTRICT_LABELS[venue.district] ?? venue.district}</span>
                <span className="text-zinc-700">&middot;</span>
                <span>{venue.address}</span>
                <ConfidenceDots confidence={venue.karaoke_schedule_confidence} />
                <DistanceBadge km={distanceKm} />
              </div>
            </div>
          </div>

          {description && (
            <p className="text-[13px] text-zinc-400 line-clamp-2 leading-relaxed mb-1.5 ml-12">{description}</p>
          )}

          {getScheduleNote(venue, locale) && !description && (
            <ScheduleNote note={getScheduleNote(venue, locale)} className="ml-12 mb-1.5" />
          )}

          <div className="flex items-center justify-between gap-2 ml-12">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="px-2 py-0.5 rounded text-[11px] bg-violet-500/10 text-violet-400 font-medium">
                {t(locale, `venue_type_${venue.venue_type}`)}
              </span>
              {venue.price_range && (
                <span className="px-2 py-0.5 rounded text-[11px] bg-amber-500/10 text-amber-400 font-medium">
                  {getPriceLabel(venue.price_range)}
                </span>
              )}
              {venue.has_private_rooms && (
                <span className="px-2 py-0.5 rounded text-[11px] bg-white/[0.05] text-zinc-400">
                  {t(locale, "private_rooms")}
                </span>
              )}
            </div>
            <WeeklyGrid venue={venue} />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
